import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaSearch } from "react-icons/fa";
import useRequestCharter from "../lib/useRequestCharter";
import useFetchVesselById from "../lib/useFetchVesselById";
import { useToast } from "../../Context/ToastContext";
import Skeleton from "react-loading-skeleton";
import "./RequestCharter.css";

// Major world cities data
const worldCities = [
  // African Cities
  { name: "Lagos", country: "Nigeria", lat: 6.5244, lng: 3.3792, population: 15388000 },
  { name: "Cairo", country: "Egypt", lat: 30.0444, lng: 31.2357, population: 9500000 },
  { name: "Johannesburg", country: "South Africa", lat: -26.2041, lng: 28.0473, population: 5635000 },
  { name: "Nairobi", country: "Kenya", lat: -1.2921, lng: 36.8219, population: 4397000 },
  { name: "Casablanca", country: "Morocco", lat: 33.5731, lng: -7.5898, population: 3356000 },
  
  // USA Cities
  { name: "New York", country: "USA", lat: 40.7128, lng: -74.0060, population: 8336817 },
  { name: "Los Angeles", country: "USA", lat: 34.0522, lng: -118.2437, population: 3980400 },
  { name: "Chicago", country: "USA", lat: 41.8781, lng: -87.6298, population: 2716000 },
  { name: "Houston", country: "USA", lat: 29.7604, lng: -95.3698, population: 2320268 },
  { name: "Miami", country: "USA", lat: 25.7617, lng: -80.1918, population: 467963 },
  
  // UK Cities
  { name: "London", country: "UK", lat: 51.5074, lng: -0.1278, population: 8982000 },
  { name: "Manchester", country: "UK", lat: 53.4808, lng: -2.2426, population: 547627 },
  { name: "Birmingham", country: "UK", lat: 52.4862, lng: -1.8904, population: 1141816 },
  { name: "Liverpool", country: "UK", lat: 53.4084, lng: -2.9916, population: 498042 },
  
  // German Cities
  { name: "Berlin", country: "Germany", lat: 52.5200, lng: 13.4050, population: 3769000 },
  { name: "Hamburg", country: "Germany", lat: 53.5511, lng: 9.9937, population: 1899160 },
  { name: "Munich", country: "Germany", lat: 48.1351, lng: 11.5820, population: 1488202 },
  { name: "Frankfurt", country: "Germany", lat: 50.1109, lng: 8.6821, population: 753056 },
  
  // Other Major Cities
  { name: "Tokyo", country: "Japan", lat: 35.6762, lng: 139.6503, population: 13960000 },
  { name: "Sydney", country: "Australia", lat: -33.8688, lng: 151.2093, population: 5312000 },
  { name: "Singapore", country: "Singapore", lat: 1.3521, lng: 103.8198, population: 5850342 },
  { name: "Dubai", country: "UAE", lat: 25.2048, lng: 55.2708, population: 3412000 }
];

const initialState = {
  origin: "",
  destination: "",
  location: "",
  startDate: "",
  duration: "",
  extensionPeriodNotice: "",
  scopeOfWork: "",
  security: "",
  restrictionsDraft: "",
  technicalRequirements: "",
  numberOfPassengers: "",
  otherRequirements: "",
};

const RequestCharter = () => {
  const { vesselId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [extensionEnabled, setExtensionEnabled] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedFromLocation, setSelectedFromLocation] = useState(null);
  const [selectedToLocation, setSelectedToLocation] = useState(null);
  const [selectingLocation, setSelectingLocation] = useState('from'); // 'from' or 'to'
  const [searchQuery, setSearchQuery] = useState('');
  const { mutate, isLoading, isSuccess, isError, error } = useRequestCharter();
  const { vessel: vesselInfo, vesselLoading } = useFetchVesselById(vesselId);
  const { showToast } = useToast();

  // Filter cities based on search query
  const filteredCities = useMemo(() => {
    if (!searchQuery.trim()) {
      return worldCities.slice(0, 16); // Show all cities by default
    }
    
    return worldCities
      .filter(city => 
        city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        city.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
        `${city.name}, ${city.country}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => b.population - a.population);
  }, [searchQuery]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };


  const handleMapSelection = (type) => {
    setSelectingLocation(type);
    setShowMap(true);
  };

  const handleMapClose = () => {
    setShowMap(false);
    setSelectedFromLocation(null);
    setSelectedToLocation(null);
    setSearchQuery('');
  };

  const handleLocationSelect = (coordinates, locationName) => {
    if (selectingLocation === 'from') {
      setSelectedFromLocation({ coordinates, name: locationName });
      setForm((prev) => ({ ...prev, origin: `${coordinates.lat},${coordinates.lng}` }));
    } else {
      setSelectedToLocation({ coordinates, name: locationName });
      setForm((prev) => ({ ...prev, destination: `${coordinates.lat},${coordinates.lng}` }));
    }
    setShowMap(false);
  };

 


  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!form.startDate) {
      alert("Start date is required");
      return;
    }
    if (!form.duration) {
      alert("Duration is required");
      return;
    }
    if (!form.scopeOfWork) {
      alert("Scope of work is required");
      return;
    }
    
    // Validate coordinates
    if (!form.origin || !form.destination) {
      alert("Both origin and destination coordinates are required");
      return;
    }
    
    // Build API body
    let body = {
      startDate: form.startDate,
      duration: Number(form.duration),
      scopeOfWork: form.scopeOfWork,
      technicalRequirements: form.technicalRequirements || "",
      otherRequirements: form.otherRequirements || "",
    };
    
    // Add optional fields if they have values
    if (form.numberOfPassengers) body.numberOfPassengers = Number(form.numberOfPassengers);
    if (form.security !== "") body.security = form.security === "true";
    if (form.restrictionsDraft) body.restrictionsDraft = form.restrictionsDraft;
    if (extensionEnabled && form.extensionPeriodNotice) body.extensionPeriodNotice = Number(form.extensionPeriodNotice);
    
    // Handle coordinates
    const originCoords = form.origin.split(",").map((v) => parseFloat(v.trim()));
    const destCoords = form.destination.split(",").map((v) => parseFloat(v.trim()));
    if (
      originCoords.length === 2 &&
      destCoords.length === 2 &&
      originCoords.every((n) => !isNaN(n)) &&
      destCoords.every((n) => !isNaN(n))
    ) {
      body.origin = { coordinates: originCoords };
      body.destination = { coordinates: destCoords };
    } else {
      alert("Invalid coordinates format. Use: lat,lng (e.g., 40.7128,-74.0060)");
      return;
    }
    
    console.log("Submitting charter request:", body);
    mutate({ vesselId, body });
  };

  // Handle success and error states with toast
  useEffect(() => {
    if (isSuccess) {
      showToast("Charter request submitted successfully!", "success");
      // Navigate to ongoing process page after successful submission
      navigate("/charterer-dashboard/ongoing-process");
    }
  }, [isSuccess, showToast, navigate]);

  useEffect(() => {
    if (isError && error) {
      showToast(error.message || "Failed to submit charter request", "error");
    }
  }, [isError, error, showToast]);

  return (
    <>
    <form className="request-charter-form" onSubmit={handleSubmit}>
      <h2>Request a Charter</h2>
      {/* Vessel Card */}
      {vesselLoading ? (
        <Skeleton height={120} />
      ) : vesselInfo ? (
        <div className="vessel-card">
          <img src={vesselInfo.images?.[0]} alt="Vessel" className="vessel-img" />
          <div className="vessel-details">
            <div className="vessel-title-row">
              <div className="vessel-name">
                <b>{vesselInfo.primaryInformation?.vesselType || "Vessel"}</b> {vesselInfo.primaryInformation?.vesselName ? `'${vesselInfo.primaryInformation.vesselName}'` : ""}
              </div>
              <div className="vessel-price">${vesselInfo.primaryInformation?.pricing?.toLocaleString() || "0"}/Day</div>
            </div>
            <div className="vessel-specs">
              <b>Specifications</b><br />
              <span className="spec-blue">{vesselInfo.vesselInformation?.bollardPull || "??"}T Bollard Pull</span><br />
              <span>{vesselInfo.vesselInformation?.numberOfPassengers || "??"} Pax Accommodation</span><br />
              <span>{vesselInfo.vesselInformation?.deckSpace || "??"} m² Deck Space</span><br />
              <span>{vesselInfo.vesselInformation?.deckStrength || "??"}T/m² Deck Strength</span><br />
              <span>{vesselInfo.vesselInformation?.bhp || "??"} BHP</span><br />
              <span>Classification: {vesselInfo.vesselInformation?.classification || "??"}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="error-message">Vessel not found</div>
      )}

      {/* Shipping Coordinates */}
      <div className="form-section">
        <h3>Shipping Coordinates</h3>
        <div className="row">
          <div className="col">
            <label>From Location *</label>
            <input 
              name="origin" 
              value={selectedFromLocation ? selectedFromLocation.name : form.origin} 
              onChange={handleChange}
              placeholder="Select from location on map" 
              readOnly
              style={{ cursor: "pointer" }}
              onClick={() => handleMapSelection('from')}
              required 
            />
          </div>
          <div className="col">
            <label>To Location *</label>
            <input 
              name="destination" 
              value={selectedToLocation ? selectedToLocation.name : form.destination} 
              onChange={handleChange}
              placeholder="Select to location on map" 
              readOnly
              style={{ cursor: "pointer" }}
              onClick={() => handleMapSelection('to')}
              required 
            />
          </div>
        </div>
        
        {/* Manual Input Option */}
        <div className="or-row">OR</div>
        <div className="row">
          <div className="col">
            <label>Manual Origin Input</label>
            <input name="origin" value={form.origin} onChange={handleChange} placeholder="Enter coordinates manually" />
          </div>
          <div className="col">
            <label>Manual Destination Input</label>
            <input name="destination" value={form.destination} onChange={handleChange} placeholder="Enter coordinates manually" />
          </div>
        </div>
      </div>

      {/* Charter Duration */}
      <div className="form-section">
        <h3>Charter Duration</h3>
        <div className="row">
          <div className="col">
            <label>Start date *</label>
            <input name="startDate" type="datetime-local" value={form.startDate} onChange={handleChange} required placeholder="Select" />
          </div>
          <div className="col">
            <label>Duration (Days) *</label>
            <input name="duration" type="number" value={form.duration} onChange={handleChange} required min="1" placeholder="Enter days" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Extension period</label>
            <select 
              value={extensionEnabled ? "enabled" : "disabled"} 
              onChange={(e) => setExtensionEnabled(e.target.value === "enabled")}
              style={{ padding: '14px 12px', border: '1.5px solid #e0e4ea', borderRadius: '8px', fontSize: '1rem', background: '#f8fafc' }}
            >
              <option value="disabled">No</option>
              <option value="enabled">Yes</option>
            </select>
          </div>
          <div className="col">
            <label>Extension duration (Days)</label>
            <input name="extensionPeriodNotice" type="number" value={form.extensionPeriodNotice} onChange={handleChange} disabled={!extensionEnabled} min="1" placeholder="Enter days" />
          </div>
        </div>
      </div>

      {/* More Information */}
      <div className="form-section">
        <h3>More information</h3>
        <div className="row full-width">
          <div className="col">
            <label>Detailed scope of work *</label>
            <textarea name="scopeOfWork" value={form.scopeOfWork} onChange={handleChange} required placeholder="Enter" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Security need?</label>
            <select name="security" value={form.security} onChange={handleChange}>
              <option value="">Select</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="col">
            <label>Restrictions/no-restrictions draft</label>
            <input name="restrictionsDraft" value={form.restrictionsDraft} onChange={handleChange} placeholder="???" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Number of passengers</label>
            <input name="numberOfPassengers" type="number" value={form.numberOfPassengers} onChange={handleChange} min="1" placeholder="Enter" />
          </div>
        </div>
        <div className="row full-width">
          <div className="col">
            <label>Detailed specific vessel technical requirements</label>
            <textarea name="technicalRequirements" value={form.technicalRequirements} onChange={handleChange} placeholder="Enter" />
          </div>
        </div>
        <div className="row full-width">
          <div className="col">
            <label>Other information</label>
            <textarea name="otherRequirements" value={form.otherRequirements} onChange={handleChange} placeholder="Enter" />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="back-btn" onClick={() => window.history.back()}>Back</button>
        <button type="submit" className="submit-btn" disabled={isLoading}>
          {isLoading ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>

    {/* Map Selection Modal */}
    {showMap && (
      <div className="map-modal-overlay">
        <div className="map-modal">
          <div className="map-modal-header">
            <h3>Select {selectingLocation === 'from' ? 'From' : 'To'} Location</h3>
            <button className="close-btn" onClick={handleMapClose}>×</button>
          </div>
          <div className="map-container">
            <div className="interactive-map">
              <div className="map-header">
                <p>Choose your {selectingLocation} location:</p>
                <div className="search-container">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search cities worldwide..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="city-search-input"
                  />
                </div>
              </div>
              
              <div className="location-grid">
                {filteredCities.length > 0 ? (
                  filteredCities.map((city, index) => (
                    <div 
                      key={`${city.name}-${city.country}-${index}`}
                      className="location-option" 
                      onClick={() => handleLocationSelect(
                        { lat: city.lat, lng: city.lng }, 
                        `${city.name}, ${city.country}`
                      )}
                    >
                      <FaMapMarkerAlt className="location-marker" />
                      <div className="location-info">
                        <h4>{city.name}, {city.country}</h4>
                        <p>{city.lat > 0 ? `${city.lat.toFixed(4)}°N` : `${Math.abs(city.lat).toFixed(4)}°S`}, {city.lng > 0 ? `${city.lng.toFixed(4)}°E` : `${Math.abs(city.lng).toFixed(4)}°W`}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    <p>No cities found matching your search.</p>
                    <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                      Clear Search
                    </button>
                  </div>
                )}
              </div>
              
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default RequestCharter; 